import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { US_STATES, CODE_TO_NAME } from "@/lib/states";
import { detectUsState } from "@/lib/geo";

function formatDetail(d) {
    if (!d) return "Something went wrong";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map(x => x.msg || JSON.stringify(x)).join(" ");
    return String(d);
}

export const AuthShell = ({ chapter, title, subtitle, children }) => (
    <div className="min-h-screen pt-[68px] relative overflow-hidden" data-testid="auth-shell">
        <div className="absolute inset-0 -z-10">
            <div className="absolute inset-0 grid-lines opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-void to-[#050505]" />
        </div>
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 grid md:grid-cols-2 gap-16 items-start">
            <div>
                <div className="overline mb-6">[ {chapter} ]</div>
                <h1 className="font-display font-black tracking-tighter text-5xl md:text-7xl leading-[0.88]">{title}</h1>
                <p className="mt-8 text-secondary max-w-md">{subtitle}</p>
                <div className="mt-16 hidden md:block">
                    <img src="/brand/watermark.png" alt="" className="w-[60%] max-w-md opacity-30 drop-shadow-[0_0_40px_rgba(168,85,247,0.35)]" />
                </div>
            </div>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="border border-white/10 clip-frame bg-[#0A0A0A]/80 backdrop-blur-xl p-8 md:p-12">
                {children}
            </motion.div>
        </div>
    </div>
);

export const AuthField = ({ label, children }) => (
    <label className="block">
        <span className="overline text-white/40 mb-2 block">{label}</span>
        {children}
    </label>
);

export const AuthInput = (props) => (
    <input {...props} className="w-full bg-transparent border border-white/15 focus:border-volt outline-none px-4 py-3 font-body text-white placeholder-white/25" />
);

export function Login() {
    const nav = useNavigate();
    const { login } = useAuth();
    const [form, setForm] = useState({ email: "", password: "" });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await login(form.email, form.password);
            toast.success("Welcome back");
            nav("/tournaments");
        } catch (err) {
            toast.error(formatDetail(err.response?.data?.detail) || "Login failed");
        } finally { setBusy(false); }
    };

    return (
        <AuthShell chapter="01 · Sign in" title="Back to the arena." subtitle="Your rank is still where you left it. Enter to continue the climb.">
            <form onSubmit={submit} className="space-y-5" data-testid="login-form">
                <AuthField label="Email">
                    <AuthInput type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="login-email-input" />
                </AuthField>
                <AuthField label="Password">
                    <AuthInput type="password" required value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="login-password-input" />
                </AuthField>
                <button type="submit" disabled={busy} className="btn-primary w-full text-center disabled:opacity-40" data-testid="login-submit-btn">
                    {busy ? "Signing in…" : "Sign in →"}
                </button>
                <GoogleSignInButton testId="login-google-btn" />
                <div className="pt-4 border-t border-white/5 text-sm text-white/60">
                    No account? <Link to="/register" className="text-volt hover:underline">Register a team</Link>
                </div>
            </form>
        </AuthShell>
    );
}

export function Register() {
    const nav = useNavigate();
    const { register } = useAuth();
    const [form, setForm] = useState({ email: "", password: "", name: "", gamer_tag: "", state: "", xbox_gamertag: "", psn_id: "", steam_id: "", discord_tag: "" });
    const [idFile, setIdFile] = useState(null);
    const [agreed, setAgreed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [stateAutoFilled, setStateAutoFilled] = useState(false);

    // Auto-detect US state from IP on mount, only prefill if user hasn't already touched the field
    useEffect(() => {
        let cancelled = false;
        detectUsState().then(code => {
            if (cancelled || !code) return;
            setForm(f => (f.state ? f : { ...f, state: code }));
            setStateAutoFilled(true);
        });
        return () => { cancelled = true; };
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!agreed) { toast.error("You must agree to the rulebook"); return; }
        setBusy(true);
        try {
            await register({ ...form, agreed_to_rules: true });
            // Optionally upload ID
            if (idFile) {
                const fd = new FormData();
                fd.append("file", idFile);
                try {
                    await (await import("@/lib/api")).default.post("/auth/upload-id", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    toast.success("Account created · ID under review");
                } catch (upErr) {
                    toast.success("Account created");
                    toast.error(formatDetail(upErr.response?.data?.detail) || "ID upload failed — you can add it later");
                }
            } else {
                toast.success("Account created");
            }
            nav("/teams");
        } catch (err) {
            toast.error(formatDetail(err.response?.data?.detail) || "Registration failed");
        } finally { setBusy(false); }
    };

    return (
        <AuthShell chapter="02 · Enter arena" title="Register your name." subtitle="ID verification for paid events to prevent cheaters and outsiders. Cheaters will have their ID and gamertag banned and be banned from all tournament sites. Outsiders will just be signed up where their state is located.">
            <form onSubmit={submit} className="space-y-5" data-testid="register-form">
                <AuthField label="Full name">
                    <AuthInput required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="reg-name-input" />
                </AuthField>
                <AuthField label="Gamer tag">
                    <AuthInput required value={form.gamer_tag} onChange={e => setForm({ ...form, gamer_tag: e.target.value })} placeholder="Void Shot" data-testid="reg-tag-input" />
                </AuthField>
                <AuthField label="Home state">
                    <select value={form.state} onChange={e => { setForm({ ...form, state: e.target.value }); setStateAutoFilled(false); }}
                        data-testid="reg-state-select"
                        className="w-full bg-[#0A0A0A] border border-white/15 focus:border-volt outline-none px-4 py-3 font-body text-white">
                        <option value="">— Select state —</option>
                        {US_STATES.map(([code, name]) => (
                            <option key={code} value={code}>{name}</option>
                        ))}
                    </select>
                    {stateAutoFilled && form.state && (
                        <div className="mt-2 font-mono text-[10px] tracking-[0.22em] uppercase text-volt" data-testid="reg-state-autofilled">
                            Auto-detected from your location ✓
                        </div>
                    )}
                    {form.state && !stateAutoFilled && (
                        <div className="mt-2 font-mono text-[10px] tracking-[0.22em] uppercase text-volt" data-testid="reg-state-locked">
                            Locked to the {CODE_TO_NAME[form.state] || form.state} circuit ✓
                        </div>
                    )}
                </AuthField>

                {/* Caution / ID policy notice */}
                <div className="border border-signal/40 bg-signal/[0.06] clip-frame-sm px-4 py-3 flex items-start gap-3" data-testid="reg-id-caution">
                    <span aria-hidden className="mt-[2px] w-6 h-6 flex-shrink-0 flex items-center justify-center border border-signal text-signal font-display font-black text-sm">!</span>
                    <div className="font-mono text-[10px] md:text-[11px] tracking-[0.18em] uppercase leading-relaxed text-signal">
                        To enter paid events ID must be verified. If under the age of 16, parent ID may be used.
                    </div>
                </div>

                {/* Platform links (optional) */}
                <div className="pt-4 border-t border-white/5" data-testid="reg-platform-section">
                    <div className="flex items-baseline justify-between mb-3">
                        <span className="overline text-white/70">Link platforms</span>
                        <span className="font-mono text-[9px] tracking-[0.24em] uppercase text-white/30">Optional</span>
                    </div>
                    <p className="font-mono text-[10px] tracking-[0.16em] uppercase text-white/40 mb-4 leading-relaxed">
                        Attach your Xbox, PSN, or Steam handles so opponents and admins can spot you across every lobby.
                    </p>
                    <div className="grid gap-3">
                        <PlatformField
                            label="Xbox Live gamertag" placeholder="e.g. VoidShot#1234"
                            value={form.xbox_gamertag}
                            onChange={(v) => setForm({ ...form, xbox_gamertag: v })}
                            iconColor="#107C10"
                            iconLetter="X"
                            testId="reg-xbox-input"
                        />
                        <PlatformField
                            label="PlayStation Network ID" placeholder="e.g. voidshot_ps"
                            value={form.psn_id}
                            onChange={(v) => setForm({ ...form, psn_id: v })}
                            iconColor="#0070D1"
                            iconLetter="P"
                            testId="reg-psn-input"
                        />
                        <PlatformField
                            label="Steam ID or profile URL" placeholder="e.g. 76561198000000000"
                            value={form.steam_id}
                            onChange={(v) => setForm({ ...form, steam_id: v })}
                            iconColor="#00ADEE"
                            iconLetter="S"
                            testId="reg-steam-input"
                        />
                        <PlatformField
                            label="Discord tag" placeholder="e.g. voidshot#4402 or voidshot"
                            value={form.discord_tag}
                            onChange={(v) => setForm({ ...form, discord_tag: v })}
                            iconColor="#5865F2"
                            iconLetter="D"
                            testId="reg-discord-input"
                        />
                    </div>
                </div>

                {/* ID Verification */}
                <div className="pt-4 border-t border-white/5">
                    <div className="flex items-center justify-between mb-2">
                        <span className="overline text-white/40">State ID (recommended)</span>
                        <span className="font-mono text-[9px] tracking-[0.24em] uppercase text-white/30">JPG · PNG · PDF · ≤5MB</span>
                    </div>
                    <label htmlFor="reg-id-input" className="block cursor-pointer" data-testid="reg-id-dropzone">
                        <div className={`border border-dashed clip-frame-sm px-4 py-5 text-center transition-colors ${idFile ? "border-volt bg-volt/5" : "border-white/15 hover:border-white/35"}`}>
                            {idFile ? (
                                <>
                                    <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-volt">✓ {idFile.name}</div>
                                    <div className="mt-1 font-mono text-[10px] text-white/50">{(idFile.size / 1024).toFixed(0)} KB · click to replace</div>
                                </>
                            ) : (
                                <>
                                    <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/70">Upload state ID or driver's license</div>
                                    <div className="mt-1 font-mono text-[10px] text-white/40">Required to lock in your home-state bracket slot</div>
                                </>
                            )}
                        </div>
                    </label>
                    <input
                        id="reg-id-input"
                        type="file"
                        accept="image/jpeg,image/png,image/webp,application/pdf"
                        onChange={e => setIdFile(e.target.files?.[0] || null)}
                        data-testid="reg-id-input"
                        className="hidden"
                    />
                </div>

                <AuthField label="Email">
                    <AuthInput type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="reg-email-input" />
                </AuthField>
                <AuthField label="Password (min 6)">
                    <AuthInput type="password" required minLength={6} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="reg-password-input" />
                </AuthField>

                {/* Rules acceptance */}
                <label htmlFor="reg-rules-checkbox" className="flex items-start gap-3 cursor-pointer group pt-2" data-testid="reg-rules-agree-wrapper">
                    <input
                        id="reg-rules-checkbox"
                        type="checkbox"
                        checked={agreed}
                        onChange={e => setAgreed(e.target.checked)}
                        data-testid="reg-rules-checkbox"
                        className="sr-only"
                    />
                    <span className={`mt-[3px] w-4 h-4 flex-shrink-0 flex items-center justify-center border transition-colors ${agreed ? "bg-volt border-volt text-void" : "border-white/25 bg-transparent group-hover:border-white/60"}`}>
                        <span className="font-mono text-[10px] leading-none">{agreed ? "✓" : ""}</span>
                    </span>
                    <span className="text-sm text-white/70 leading-relaxed">
                        I agree to the <Link to="/rules" target="_blank" rel="noopener noreferrer" className="text-volt underline underline-offset-4 hover:text-white transition-colors" data-testid="reg-rules-link">official rulebook</Link>, including anti-cheat, sportsmanship, and prize-payment terms.
                    </span>
                </label>

                <button type="submit" disabled={busy || !agreed} className="btn-primary w-full text-center disabled:opacity-40 disabled:cursor-not-allowed" data-testid="register-submit-btn">
                    {busy ? (idFile ? "Creating & verifying…" : "Creating…") : !agreed ? "Agree to rules to continue" : "Enter Arena →"}
                </button>
                <GoogleSignInButton testId="register-google-btn" />
                <div className="pt-4 border-t border-white/5 text-sm text-white/60">
                    Already have an account? <Link to="/login" className="text-volt hover:underline">Sign in</Link>
                </div>
            </form>
        </AuthShell>
    );
}


const PlatformField = ({ label, placeholder, value, onChange, iconColor, iconLetter, testId }) => (
    <label className="flex items-center gap-3 border border-white/10 bg-[#0A0A0A] px-3 py-2 focus-within:border-volt/60 transition-colors">
        <span
            aria-hidden
            className="w-9 h-9 flex-shrink-0 flex items-center justify-center font-display font-black text-lg"
            style={{ background: iconColor, color: "#fff" }}
        >
            {iconLetter}
        </span>
        <div className="flex-1 min-w-0">
            <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-white/40">{label}</div>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testId}
                maxLength={64}
                className="w-full bg-transparent outline-none font-body text-sm text-white placeholder-white/25 pt-0.5"
            />
        </div>
    </label>
);

const GoogleSignInButton = ({ testId }) => {
    const startGoogle = () => {
        const redirect = `${window.location.origin}/auth/callback`;
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
    };
    return (
        <div className="pt-2">
            <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 h-px bg-white/10" />
                <span className="overline text-white/35">Or</span>
                <div className="flex-1 h-px bg-white/10" />
            </div>
            <button
                type="button"
                onClick={startGoogle}
                data-testid={testId}
                className="w-full flex items-center justify-center gap-3 bg-white text-[#0A0A0A] font-body font-medium text-sm py-3 px-4 hover:bg-white/90 transition-colors"
            >
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                </svg>
                Continue with Google
            </button>
        </div>
    );
};

